import React from 'react';
import { Box, Typography, Chip, alpha } from '@mui/material';
import { 
    Person as PersonIcon, 
    FactCheckOutlined as FactCheckIcon,
    AccessTime as AccessTimeIcon,
    Email as EmailIcon,
    PhonelinkRing as DeviceIcon,
    Star as StarIcon
} from '@mui/icons-material';

const getScoreColor = (score: number) => {
    if (score >= 4.5) return '#5DCC0A'; 
    if (score >= 3.5) return '#FFB400'; 
    if (score >= 2.5) return '#FF8A00'; 
    if (score >= 1.5) return '#FF4C51'; 
    return '#FF4C51'; 
};

const getAnswerColor = (metricType: string, score: any) => {
    const numScore = Number(score);
    if (isNaN(numScore)) return '#ffffff';
    
    if (numScore >= 4) return '#5DCC0A';
    if (numScore === 3) return '#FFB400';
    return '#FF4C51';
};

const getNPSChipProps = (val: any) => {
    if (!val) {
        return { label: 'Sin Datos', color: '#888', bgcolor: '#f5f5f5', borderColor: '#ccc' };
    }
    if (val.metric === 'NPS' || val.name === 'NPS' || val.type === 'Promotor' || val.type === 'Pasivo' || val.type === 'Detractor') {
        if (val.type === 'Promotor') {
            return { label: 'Promotor', color: '#4ba804', bgcolor: alpha('#5DCC0A', 0.1), borderColor: alpha('#5DCC0A', 0.3) };
        }
        if (val.type === 'Pasivo') {
            return { label: 'Pasivo', color: '#d39600', bgcolor: alpha('#FFB400', 0.1), borderColor: alpha('#FFB400', 0.3) };
        }
        if (val.type === 'Detractor') {
            return { label: 'Detractor', color: '#e53e3e', bgcolor: alpha('#FF4C51', 0.1), borderColor: alpha('#FF4C51', 0.3) };
        }
        return { label: `NPS: ${val.score}%`, color: '#4ba804', bgcolor: alpha('#5DCC0A', 0.1), borderColor: alpha('#5DCC0A', 0.3) };
    }
    if (val.metric === 'CSAT' || val.name === 'CSAT' || (val.metricType === 'CSAT')) {
        return { label: `CSAT: ${val.score}%`, color: '#3f51b5', bgcolor: alpha('#3f51b5', 0.1), borderColor: alpha('#3f51b5', 0.3) };
    }
    if (val.metric === 'CES' || val.name === 'CES' || (val.metricType === 'CES')) {
        return { label: `CES: ${val.score}`, color: '#ff9800', bgcolor: alpha('#ff9800', 0.1), borderColor: alpha('#ff9800', 0.3) };
    }
    return { label: val.metric || val.name || val.type || `Score: ${val.score}`, color: '#888', bgcolor: '#f5f5f5', borderColor: '#ccc' };
};

export const ShareTicket = React.forwardRef(({ row }: any, ref: any) => {
    if (!row) return null;

    const date = new Date(row.createdAt);
    const dateString = date.toLocaleDateString();
    const timeString = date.toLocaleTimeString();

    return (
        <Box 
            ref={ref}
            sx={{ 
                position: 'absolute', 
                left: '-9999px',
                top: 0,
                width: 800, 
                bgcolor: '#ffffff',
                color: '#333333',
                p: 4,
                borderRadius: 4,
                boxShadow: '0px 10px 30px rgba(0,0,0,0.1)',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                fontFamily: 'sans-serif'
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #f0f0f0', pb: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a1a1a' }}>
                        {row.surveyName}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: '#666', mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon fontSize="small" /> {dateString} a las {timeString}
                    </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Chip 
                        icon={<StarIcon sx={{ color: `${getScoreColor(row.averageScore)} !important` }} />} 
                        label={`Promedio: ${Number(row.averageScore).toFixed(1)}`} 
                        sx={{ 
                            fontSize: '1.2rem', 
                            height: 40, 
                            fontWeight: 800, 
                            bgcolor: alpha(getScoreColor(row.averageScore), 0.1), 
                            color: getScoreColor(row.averageScore),
                            borderColor: getScoreColor(row.averageScore),
                            border: '1px solid'
                        }} 
                    />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 4, bgcolor: '#f8f9fa', p: 3, borderRadius: 2 }}>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ color: '#888', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 1 }}>
                        Información del Cliente
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <PersonIcon sx={{ color: '#555' }} />
                        <Typography sx={{ fontWeight: 600, fontSize: '1.1rem' }}>{row.clientName}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <EmailIcon sx={{ color: '#555' }} />
                        <Typography sx={{ color: '#666' }}>{row.clientEmail || 'Sin correo'}</Typography>
                    </Box>
                </Box>

                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, borderLeft: '1px solid #e0e0e0', pl: 4 }}>
                    <Typography variant="subtitle2" sx={{ color: '#888', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 1 }}>
                        Detalles de Evaluación
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <DeviceIcon sx={{ color: '#555' }} />
                        <Typography sx={{ fontWeight: 500 }}>{row.deviceName} - {row.origin || 'Sin origen'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <FactCheckIcon sx={{ color: '#555' }} />
                        <Typography sx={{ fontWeight: 500 }}>Atendido por: {row.agentName}</Typography>
                    </Box>
                </Box>
            </Box>

            {row.metrics && row.metrics.length > 0 && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                    {row.metrics.map((m: any, idx: number) => {
                        const props = getNPSChipProps(m);
                        return (
                            <Chip 
                                key={idx}
                                label={props.label} 
                                sx={{
                                    height: 32,
                                    fontSize: '0.9rem',
                                    fontWeight: 700,
                                    color: props.color,
                                    bgcolor: props.bgcolor,
                                    border: `1px solid ${props.borderColor}`
                                }} 
                            />
                        );
                    })}
                </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, borderBottom: '2px solid #f0f0f0', pb: 1 }}>
                    Respuestas ({row.answers?.length || 0}/{row.totalQuestions || row.answers?.length || 0})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {row.answers && row.answers.length > 0 ? row.answers.map((q: any, idx: number) => (
                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 3, p: 2, bgcolor: '#ffffff', border: '1px solid #eaeaea', borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#aaa', mt: 0.2 }}>{idx + 1}.</Typography>
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#222', mb: 0.5 }}>{q.questionText || q.question || q.text}</Typography>
                                    {q.metricType && (
                                        <Chip label={q.metricType} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#f0f0f0', color: '#666', fontWeight: 600 }} />
                                    )}
                                </Box>
                            </Box>
                            <Chip 
                                label={`${q.score}`} 
                                sx={{ 
                                    height: 36, 
                                    minWidth: 48, 
                                    fontSize: '1rem', 
                                    fontWeight: 800, 
                                    bgcolor: alpha(getAnswerColor(q.metricType, q.score), 0.15), 
                                    color: getAnswerColor(q.metricType, q.score), 
                                    border: '1px solid', 
                                    borderColor: alpha(getAnswerColor(q.metricType, q.score), 0.3) 
                                }} 
                            />
                        </Box>
                    )) : <Typography variant="body2" sx={{ color: '#888', fontStyle: 'italic' }}>No hay preguntas registradas</Typography>}
                </Box>
            </Box>

            {row.comments && (
                <Box sx={{ mt: 2, p: 3, bgcolor: '#f5f7f9', borderRadius: 2, borderLeft: '4px solid #3f51b5' }}>
                    <Typography variant="subtitle2" sx={{ color: '#3f51b5', textTransform: 'uppercase', fontWeight: 800, letterSpacing: 1, mb: 1 }}>
                        Comentarios del cliente
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#333', fontStyle: 'italic', lineHeight: 1.6 }}>
                        "{row.comments}"
                    </Typography>
                </Box>
            )}

            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #f0f0f0', textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 600 }}>
                    Reporte generado por QiHotels - Calificaciones
                </Typography>
            </Box>
        </Box>
    );
});
